import React, { useState, useRef, useEffect } from 'react';
import { ExtraIncome } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import FullscreenLoader from './FullScreenLoader';
import { formatCurrency } from '../util/formatCurrency';
import { toast } from 'react-toastify';
import ConfirmationModal from '../modals/ConfirmationModal';

type Props = {
  extraIncomes: ExtraIncome[];
  openActionMenuId: number | null;
  setOpenActionMenuId: (id: number | null) => void;
  onEdit: (income: ExtraIncome) => void;
  onMarkAsPaid: (income: ExtraIncome) => void;
  onSuccess: () => void;
  hideOrderDetails?: boolean;
};

const ExtraIncomeTable: React.FC<Props> = ({
  extraIncomes,
  openActionMenuId,
  setOpenActionMenuId,
  onEdit,
  onMarkAsPaid,
  onSuccess,
  hideOrderDetails = false
}) => {
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [exportingInvoice, setExportingInvoice] = useState(false);
  const [pendingExportIncomeId, setPendingExportIncomeId] = useState<number | null>(null);
  const [showExportConfirmModal, setShowExportConfirmModal] = useState(false);
  const [showNotificationConfirmModal, setShowNotificationConfirmModal] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openActionMenuId !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-menu')) {
          setOpenActionMenuId(null);
          setMenuPosition(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionMenuId, setOpenActionMenuId]);

  const formatExportDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleExportConfirm = () => {
    setShowExportConfirmModal(false);
    setShowNotificationConfirmModal(true);
  };

  const handleFinalExportConfirm = async (sendNotification: boolean) => {
    setShowNotificationConfirmModal(false);
    if (!pendingExportIncomeId) return;

    setExportingInvoice(true);
    try {
      const res = await fetchWithAuth(`/extra_incomes/${pendingExportIncomeId}/export_customer_invoice`, {
        method: 'POST',
        body: JSON.stringify({ send_notification: sendNotification }),
      });
      const data = await res.json();
      if (data.message) {
        toast.success(data.message);
        onSuccess();
      } else {
        toast.error(data.errors?.[0] ?? 'Extra income invoice export failed.');
      }
    } catch {
      toast.error('An error occurred while exporting the invoice.');
    } finally {
      setExportingInvoice(false);
      setPendingExportIncomeId(null);
    }
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="table-fixed w-full bg-white border border-gray-200 rounded-md shadow-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-sm font-semibold text-gray-700">
              <th className="hidden md:table-cell px-4 py-2">Created At</th>
              <th className="hidden md:table-cell px-4 py-2">Extra Income Reference</th>
              {!hideOrderDetails && <th className="hidden md:table-cell px-4 py-2">Order Reference</th>}
              <th className="hidden md:table-cell px-4 py-2">Date</th>
              {!hideOrderDetails && <th className="hidden md:table-cell px-4 py-2">Customer</th>}
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Amount</th>
              <th className="hidden md:table-cell px-4 py-2">Credit</th>
              <th className="hidden md:table-cell px-4 py-2">Total</th>
              <th className="hidden md:table-cell px-4 py-2">Payment Date</th>
              <th className="px-4 py-2">Paid</th>
              <th className="hidden md:table-cell px-4 py-2">Last Exported</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
        <tbody>
          {extraIncomes.map((income) => (
            <tr
              key={income.id}
              className={`border-t border-gray-100 text-sm text-gray-800 relative overflow-visible hover:bg-yellow-100 ${
                openActionMenuId === income.id ? 'bg-yellow-100' : ''
              }`}
            >
                              <td className="hidden md:table-cell px-4 py-2">
                  {new Date(income.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </td>
              <td className="hidden md:table-cell px-4 py-2">{income.extra_income_reference}</td>
              {!hideOrderDetails && <td className="hidden md:table-cell px-4 py-2">{income.order.order_reference}</td>}
              <td className="hidden md:table-cell px-4 py-2">
                {new Date(income.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </td>
              {!hideOrderDetails && <td className="hidden md:table-cell px-4 py-2">{income.customer.identifier}</td>}
              <td className="px-4 py-2">{income.income_type}</td>
              <td className="px-4 py-2">{formatCurrency(income.amount)}</td>
              <td className="hidden md:table-cell px-4 py-2">{income.credit_amount > 0 ? formatCurrency(income.credit_amount) : '—'}</td>
              <td className="hidden md:table-cell px-4 py-2">{formatCurrency(income.total_amount)}</td>
              <td className="hidden md:table-cell px-4 py-2">{income.payment_date}</td>
              <td className="px-4 py-2">
                {income.payment_date ? (
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
                ) : (
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full"></span>
                )}
              </td>
              <td className="hidden md:table-cell px-4 py-2 text-xs">
                {income.invoice_last_exported_at ? (
                  <span className="flex flex-col gap-1">
                    <span>{formatExportDate(income.invoice_last_exported_at)}</span>
                    <span className={`px-1 py-0.5 rounded text-xs w-fit ${income.invoice_last_export_notification_sent ? 'bg-green-200' : 'bg-gray-200'}`}>
                      {income.invoice_last_export_notification_sent ? 'notified' : 'no notification'}
                    </span>
                  </span>
                ) : (
                  <span className="text-gray-400">Never</span>
                )}
              </td>
              <td className="px-4 py-2 relative overflow-visible">
                <div className="relative inline-block text-left action-menu">
                  <button
                    ref={(el) => { buttonRefs.current[income.id] = el; }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newMenuId = openActionMenuId === income.id ? null : income.id;
                      setOpenActionMenuId(newMenuId);

                      if (newMenuId && buttonRefs.current[income.id]) {
                        const rect = buttonRefs.current[income.id]!.getBoundingClientRect();
                        const menuHeight = 200; // Approximate height of menu
                        const spaceBelow = window.innerHeight - rect.bottom;
                        const shouldShowAbove = spaceBelow < menuHeight;

                        setMenuPosition({
                          top: shouldShowAbove ? rect.top - menuHeight : rect.bottom + 8,
                          right: window.innerWidth - rect.right
                        });
                      }
                    }}
                    className="p-1 rounded-full hover:bg-gray-100 focus:outline-none"
                  >
                    <span className="text-xl">⋯</span>
                  </button>
                  {openActionMenuId === income.id && menuPosition && (
                    <div className="fixed w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                         style={{
                           top: `${menuPosition.top}px`,
                           right: `${menuPosition.right}px`
                         }}>
                      <div className="py-1">
                        {!income.payment_date && (
                          <button
                            onClick={() => {
                              onEdit(income);
                              setOpenActionMenuId(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Edit
                          </button>
                        )}
                        {!income.payment_date && (
                          <button
                            onClick={() => {
                              if (!income.extra_income_invoice_url) return;
                              onMarkAsPaid(income);
                              setOpenActionMenuId(null);
                            }}
                            disabled={!income.extra_income_invoice_url}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              income.extra_income_invoice_url
                                ? 'text-gray-700 hover:bg-gray-100'
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            Mark as Paid
                          </button>
                        )}
                        {!income.payment_date && (
                          <button
                            onClick={async () => {
                              setGeneratingInvoice(true);
                              setOpenActionMenuId(null);
                              try {
                                const res = await fetchWithAuth(`/extra_incomes/${income.id}/generate_invoice`, {
                                  method: 'POST'
                                });
                                const data = await res.json();
                                if (data.invoice_url) {
                                  window.open(data.invoice_url, '_blank');
                                  onSuccess();
                                } else {
                                  toast.error('Invoice generation failed');
                                }
                              } catch (err) {
                                toast.error('An error occurred while generating the invoice.');
                              } finally {
                                setGeneratingInvoice(false);
                              }
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Generate Invoice
                          </button>
                        )}
                        {!income.payment_date && (
                          <button
                            onClick={() => {
                              if (!income.extra_income_invoice_url) return;
                              setPendingExportIncomeId(income.id);
                              setOpenActionMenuId(null);
                              setShowExportConfirmModal(true);
                            }}
                            disabled={!income.extra_income_invoice_url}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              income.extra_income_invoice_url
                                ? 'text-gray-700 hover:bg-gray-100'
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {income.has_customer_invoice_pdf ? 'Re-export Invoice' : 'Export Invoice'}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            if (income.extra_income_invoice_url) {
                              window.open(income.extra_income_invoice_url, '_blank');
                            }
                          }}
                          disabled={!income.extra_income_invoice_url}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            income.extra_income_invoice_url ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          View Invoice
                        </button>
                        {!income.payment_date && (
                          <button
                            onClick={() => {
                              if (income.payment_link) {
                                window.open(income.payment_link, '_blank');
                              }
                              setOpenActionMenuId(null);
                            }}
                            disabled={!income.payment_link}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              income.payment_link ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            Yoco Link
                          </button>
                        )}
                        {!income.payment_date && (
                          <button
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this extra income?')) {
                                try {
                                  await fetchWithAuth(`/extra_incomes/${income.id}`, {
                                    method: 'DELETE',
                                  });
                                  onSuccess();
                                  toast.success('Extra income deleted successfully.');
                                } catch (error) {
                                  toast.error('Failed to delete extra income.');
                                } finally {
                                  setOpenActionMenuId(null);
                                }
                              }
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {generatingInvoice && <FullscreenLoader message="Invoice Generating..." />}
      {exportingInvoice && <FullscreenLoader message="Exporting Invoice PDF..." />}
      {showExportConfirmModal && (
        <ConfirmationModal
          isOpen={showExportConfirmModal}
          message={`Are you sure you want to ${extraIncomes.find(i => i.id === pendingExportIncomeId)?.has_customer_invoice_pdf ? 're-export' : 'export'} the customer invoice PDF?`}
          onConfirm={handleExportConfirm}
          onCancel={() => {
            setShowExportConfirmModal(false);
            setPendingExportIncomeId(null);
          }}
        />
      )}
      {showNotificationConfirmModal && (
        <ConfirmationModal
          isOpen={showNotificationConfirmModal}
          message="Would you like to send a notification?"
          onConfirm={() => handleFinalExportConfirm(true)}
          onCancel={() => handleFinalExportConfirm(false)}
        />
      )}
    </div>
  );
};

export default ExtraIncomeTable;
